/* D81 — Client Media Library: a Google-Drive-style per-client asset hub.
 *
 * Responsibilities:
 *   - Browse folders (tree on the left, breadcrumb at the top)
 *   - Upload, rename, move, delete, share (link-copy) files
 *   - Grid/list view + sort (month/name/size) + month filter + search
 *   - Preview images / PDFs / videos inline (Radix Dialog)
 *
 * Safety:
 *   - This component only ever talks to /api/client-media/...
 *   - It NEVER reads contracts data or sends emails.
 */
import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { clientMediaAPI } from '../lib/api';
import { useLiveInvalidate } from '../lib/useLiveData';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from './ui/dropdown-menu';
import {
  Folder, FolderOpen, FolderPlus, Upload, MoreVertical, ChevronRight, ChevronDown,
  Download, Edit2, Trash2, Move, Link as LinkIcon, Image as ImageIcon,
  File as FileIcon, FileText, Film, Grid3x3, List as ListIcon, Search,
  ArrowUpDown, Eye, Share2, Import,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '../lib/dateFormat';
import {
  NewFolderDialog, RenameDialog, MoveDialog, DeleteConfirm,
  ShareDialog, PreviewOverlay, AuthImg, ImportFromSubmissionsDialog,
} from './ClientMediaDialogs';

const SORT_OPTIONS = [
  { value: 'created_at_desc', label: 'Newest first' },
  { value: 'created_at_asc', label: 'Oldest first' },
  { value: 'name_asc', label: 'Name (A → Z)' },
  { value: 'name_desc', label: 'Name (Z → A)' },
  { value: 'size_desc', label: 'Size (large → small)' },
  { value: 'size_asc', label: 'Size (small → large)' },
];

function humanSize(bytes) {
  if (!bytes) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB'];
  let i = 0; let v = bytes;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}

function fileIcon(ct) {
  if (!ct) return FileIcon;
  if (ct.startsWith('image/')) return ImageIcon;
  if (ct.startsWith('video/')) return Film;
  if (ct === 'application/pdf') return FileText;
  return FileIcon;
}

function isPreviewable(ct) {
  return !!ct && (
    ct.startsWith('image/') || ct.startsWith('video/') || ct === 'application/pdf'
  );
}

// ── Folder tree (left rail) — iterative, NOT a self-referential
// component, because the visual-edits babel plugin recurses on
// JSX self-references without a depth/cycle guard. ────────────────
function FolderTree({ folders, currentFolderId, onSelect, onCreateChild, onRename, onDelete, onShare, canEdit, openSet, onToggle }) {
  // Build {parentId: [children]} and flatten depth-first into a list
  // of {folder, depth} rows so the JSX render is a single .map()
  // — no recursive component → no infinite babel traversal.
  const rows = useMemo(() => {
    const byParent = {};
    for (const f of folders) {
      const p = f.parent_id || 'ROOT';
      if (!byParent[p]) byParent[p] = [];
      byParent[p].push(f);
    }
    for (const k of Object.keys(byParent)) {
      byParent[k].sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { numeric: true, sensitivity: 'base' }));
    }
    const out = [];
    const walk = (parentKey, depth) => {
      for (const f of (byParent[parentKey] || [])) {
        const hasChildren = (byParent[f.id] || []).length > 0;
        out.push({ folder: f, depth, hasChildren });
        if (hasChildren && openSet.has(f.id)) walk(f.id, depth + 1);
      }
    };
    walk('ROOT', 0);
    return out;
  }, [folders, openSet]);

  return (
    <div className="text-[13px]" data-testid="d81-folder-tree">
      <button
        onClick={() => onSelect(null)}
        className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-foreground/[0.04] ${currentFolderId === null ? 'bg-gold/10 text-gold' : 'text-foreground/80'}`}
        data-testid="d81-folder-root"
      >
        <FolderOpen className="h-3.5 w-3.5" />
        <span className="truncate">All Media</span>
      </button>
      {rows.map(({ folder, depth, hasChildren }) => {
        const isActive = currentFolderId === folder.id;
        const isOpen = openSet.has(folder.id);
        return (
          <div
            key={folder.id}
            className={`group flex items-center gap-1 px-2 py-1.5 rounded-md hover:bg-foreground/[0.04] ${isActive ? 'bg-gold/10 text-gold' : 'text-foreground/80'}`}
            style={{ paddingLeft: 8 + depth * 14 }}
          >
            <button
              onClick={() => onToggle(folder.id)}
              className="text-muted-foreground/50 hover:text-foreground shrink-0"
              aria-label={isOpen ? 'Collapse' : 'Expand'}
            >
              {hasChildren ? (isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />) : <span className="block w-3" />}
            </button>
            <button
              onClick={() => onSelect(folder.id)}
              className="flex-1 min-w-0 flex items-center gap-2 text-left"
              data-testid={`d81-folder-node-${folder.id}`}
            >
              {isOpen ? <FolderOpen className="h-3.5 w-3.5 shrink-0" /> : <Folder className="h-3.5 w-3.5 shrink-0" />}
              <span className="truncate">{folder.name}</span>
              {folder.share_token && (
                <Share2
                  className="h-3 w-3 text-gold/80 shrink-0"
                  title={folder.share_access === 'anyone' ? 'Shared — anyone with link' : 'Shared — restricted'}
                  data-testid={`d81-folder-shared-${folder.id}`}
                />
              )}
            </button>
            {canEdit && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="opacity-0 group-hover:opacity-100 p-0.5 text-muted-foreground/70 hover:text-foreground" aria-label="Folder actions" data-testid={`d81-folder-menu-${folder.id}`}>
                    <MoreVertical className="h-3 w-3" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-card border-border/40">
                  <DropdownMenuItem onClick={() => onCreateChild(folder.id)} data-testid={`d81-folder-new-child-${folder.id}`}>
                    <FolderPlus className="h-3.5 w-3.5 mr-2" /> New subfolder
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onRename(folder)} data-testid={`d81-folder-rename-${folder.id}`}>
                    <Edit2 className="h-3.5 w-3.5 mr-2" /> Rename
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onShare(folder)} data-testid={`d81-folder-share-${folder.id}`}>
                    <Share2 className="h-3.5 w-3.5 mr-2" /> {folder.share_token ? 'Manage share link' : 'Share to client'}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => onDelete(folder)} className="text-red-400 focus:text-red-400" data-testid={`d81-folder-delete-${folder.id}`}>
                    <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete folder
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── File card / row ───────────────────────────────────────────────────
function FileCard({ file, onPreview, onMenu, viewMode }) {
  const Icon = fileIcon(file.content_type);
  const isShared = !!file.share_token;
  if (viewMode === 'list') {
    return (
      <div
        className="group flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-foreground/[0.03] border border-transparent hover:border-border/30"
        data-testid={`d81-file-row-${file.id}`}
      >
        <button onClick={() => onPreview(file)} className="flex items-center gap-3 flex-1 min-w-0 text-left">
          {file.has_thumbnail && file.thumb_url ? (
            <AuthImg
              src={file.thumb_url}
              alt=""
              className="h-9 w-9 rounded object-cover shrink-0"
              fallback={<Icon className="h-5 w-5 text-muted-foreground/60 shrink-0" />}
              data-testid={`d81-file-thumb-${file.id}`}
            />
          ) : (
            <Icon className="h-5 w-5 text-muted-foreground/60 shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-medium text-foreground truncate">{file.filename}</p>
            <p className="text-[11px] text-muted-foreground/40">
              {humanSize(file.file_size)} · {file.uploader_name || 'Team'} · {formatDate(file.created_at)}
            </p>
          </div>
        </button>
        {isShared && (
          <span title="Shared with client" className="text-gold/80" data-testid={`d81-file-shared-${file.id}`}>
            <Share2 className="h-3.5 w-3.5" />
          </span>
        )}
        <FileMenu file={file} onMenu={onMenu} />
      </div>
    );
  }
  // Grid card
  return (
    <div
      className="group rounded-xl border border-border/30 bg-card/40 hover:border-gold/40 transition-colors overflow-hidden"
      data-testid={`d81-file-card-${file.id}`}
    >
      <button onClick={() => onPreview(file)} className="block w-full aspect-square bg-foreground/[0.02] flex items-center justify-center overflow-hidden">
        {file.has_thumbnail && file.thumb_url ? (
          <AuthImg
            src={file.thumb_url}
            alt=""
            className="w-full h-full object-cover"
            fallback={<Icon className="h-10 w-10 text-muted-foreground/40" />}
            data-testid={`d81-file-thumb-${file.id}`}
          />
        ) : (
          <Icon className="h-10 w-10 text-muted-foreground/40" />
        )}
      </button>
      <div className="flex items-center gap-2 px-2.5 py-2">
        <div className="flex-1 min-w-0">
          <p className="text-[12px] font-medium text-foreground truncate" title={file.filename}>{file.filename}</p>
          <p className="text-[10px] text-muted-foreground/40">{humanSize(file.file_size)}</p>
        </div>
        {isShared && <Share2 className="h-3 w-3 text-gold/80" title="Shared" />}
        <FileMenu file={file} onMenu={onMenu} />
      </div>
    </div>
  );
}

function FileMenu({ file, onMenu }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="p-1 text-muted-foreground/40 hover:text-foreground"
          aria-label="File actions"
          data-testid={`d81-file-menu-${file.id}`}
          onClick={(e) => e.stopPropagation()}
        >
          <MoreVertical className="h-3.5 w-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-card border-border/40">
        <DropdownMenuItem onClick={() => onMenu('preview', file)} data-testid={`d81-file-action-preview-${file.id}`}>
          <Eye className="h-3.5 w-3.5 mr-2" /> Preview
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onMenu('download', file)} data-testid={`d81-file-action-download-${file.id}`}>
          <Download className="h-3.5 w-3.5 mr-2" /> Download
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => onMenu('rename', file)} data-testid={`d81-file-action-rename-${file.id}`}>
          <Edit2 className="h-3.5 w-3.5 mr-2" /> Rename
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onMenu('move', file)} data-testid={`d81-file-action-move-${file.id}`}>
          <Move className="h-3.5 w-3.5 mr-2" /> Move to folder
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onMenu('share', file)} data-testid={`d81-file-action-share-${file.id}`}>
          <LinkIcon className="h-3.5 w-3.5 mr-2" /> {file.share_token ? 'Manage share link' : 'Share to client'}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => onMenu('delete', file)} className="text-red-400 focus:text-red-400" data-testid={`d81-file-action-delete-${file.id}`}>
          <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ── Main library ───────────────────────────────────────────────────
export default function ClientMediaLibrary({ workspaceId, clientId, canEdit }) {
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [currentFolderId, setCurrentFolderId] = useState(null); // null = root
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
  const [sort, setSort] = useState('created_at_desc');
  const [filterMonth, setFilterMonth] = useState('');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  // Per-file upload queue: [{key, name, pct, status: 'queued'|'uploading'|'done'|'error'}]
  const [uploadQueue, setUploadQueue] = useState([]);
  const uploading = uploadQueue.some(u => u.status === 'queued' || u.status === 'uploading');
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const fileInputRef = useRef(null);

  // Dialog states
  const [newFolderDialog, setNewFolderDialog] = useState(null); // {parent_id} or null
  const [renameDialog, setRenameDialog] = useState(null); // {kind:'folder'|'file', target}
  const [moveDialog, setMoveDialog] = useState(null); // {file}
  const [deleteDialog, setDeleteDialog] = useState(null); // {kind, target}
  const [shareDialog, setShareDialog] = useState(null); // { kind: 'file'|'folder', target, share }
  const [previewFile, setPreviewFile] = useState(null);
  // Open/closed state for folder tree nodes (start: all open).
  const [openSet, setOpenSet] = useState(() => new Set());
  const onToggleFolder = useCallback((id) => {
    setOpenSet(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }, []);

  const loadFolders = useCallback(async () => {
    try {
      const r = await clientMediaAPI.listFolders(workspaceId, clientId);
      const list = r.data || [];
      setFolders(list);
      // Default all folders to open on first load.
      setOpenSet(prev => prev.size ? prev : new Set(list.map(f => f.id)));
    } catch (e) {
      toast.error('Failed to load folders');
    }
  }, [workspaceId, clientId]);

  const loadFiles = useCallback(async (opts = {}) => {
    if (!opts.silent) setLoading(true);
    try {
      const r = await clientMediaAPI.listFiles(workspaceId, clientId, {
        folder_id: currentFolderId || 'root',
        sort,
        filter_month: filterMonth || undefined,
        q: query || undefined,
      });
      setFiles(r.data || []);
    } catch (e) {
      toast.error('Failed to load files');
    } finally {
      setLoading(false);
    }
  }, [workspaceId, clientId, currentFolderId, sort, filterMonth, query]);

  useEffect(() => { loadFolders(); }, [loadFolders]);
  useEffect(() => { loadFiles(); }, [loadFiles]);

  // D84 — Real-time push: any teammate uploading / renaming / moving /
  // deleting / sharing a file or folder fires a `media` invalidate; we
  // silently refresh both lists. Falls back to the existing focus/event
  // mechanisms when the WS is down.
  useLiveInvalidate('media', useCallback(() => {
    loadFolders();
    loadFiles({ silent: true });
  }, [loadFolders, loadFiles]));

  // Breadcrumb path from current folder to root.
  const breadcrumb = useMemo(() => {
    if (!currentFolderId) return [];
    const idx = {};
    for (const f of folders) idx[f.id] = f;
    const out = [];
    let cur = idx[currentFolderId];
    while (cur) {
      out.unshift(cur);
      cur = cur.parent_id ? idx[cur.parent_id] : null;
    }
    return out;
  }, [folders, currentFolderId]);

  // ── Upload — per-file progress, no page refresh mid-batch. Each finished
  // file is added to the grid immediately; a silent reconcile load at the
  // end re-applies the active sort/filter without flashing "Loading…". ──
  const handleUpload = useCallback(async (fileList) => {
    const arr = Array.from(fileList || []).filter(f => f && f.size > 0);
    if (!arr.length) return;
    const oversize = arr.filter(f => f.size > 100 * 1024 * 1024);
    if (oversize.length) {
      toast.error(
        `${oversize.length} file${oversize.length > 1 ? 's' : ''} exceed the 100MB cap`,
        { description: oversize.map(f => f.name).join(', '), duration: 6000 }
      );
    }
    const okFiles = arr.filter(f => f.size <= 100 * 1024 * 1024);
    if (!okFiles.length) return;
    const queue = okFiles.map((f, i) => ({
      key: `${Date.now()}-${i}`, name: f.name, pct: 0, status: 'queued',
    }));
    setUploadQueue(queue);
    let ok = 0;
    for (let i = 0; i < okFiles.length; i++) {
      const key = queue[i].key;
      setUploadQueue(q => q.map(u => (u.key === key ? { ...u, status: 'uploading' } : u)));
      try {
        const r = await clientMediaAPI.uploadFile(
          workspaceId, clientId, okFiles[i], currentFolderId || null,
          (e) => {
            const pct = e.total ? Math.round((e.loaded * 100) / e.total) : 0;
            setUploadQueue(q => q.map(u => (u.key === key ? { ...u, pct } : u)));
          }
        );
        ok++;
        setUploadQueue(q => q.map(u => (u.key === key ? { ...u, pct: 100, status: 'done' } : u)));
        // Add the finished file to the grid immediately — no waiting for the batch.
        if (r?.data) setFiles(prev => [r.data, ...prev.filter(f => f.id !== r.data.id)]);
      } catch (err) {
        const detail = err?.response?.data?.detail;
        setUploadQueue(q => q.map(u => (u.key === key ? { ...u, status: 'error' } : u)));
        toast.error(typeof detail === 'string' ? detail : `Failed to upload ${okFiles[i].name}`);
      }
    }
    if (ok) toast.success(`${ok} file${ok > 1 ? 's' : ''} uploaded`);
    loadFiles({ silent: true });
    setTimeout(() => setUploadQueue([]), 2000);
  }, [workspaceId, clientId, currentFolderId, loadFiles]);

  // ── Folder actions ──────────────────────────────────────────────
  const createFolder = async (name, parent_id) => {
    try {
      await clientMediaAPI.createFolder(workspaceId, clientId, name, parent_id || null);
      toast.success('Folder created');
      loadFolders();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Folder create failed');
    }
  };
  const renameFolder = async (folder, name) => {
    try {
      await clientMediaAPI.updateFolder(workspaceId, clientId, folder.id, { name });
      toast.success('Renamed');
      loadFolders();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rename failed');
    }
  };
  const deleteFolder = async (folder) => {
    try {
      await clientMediaAPI.deleteFolder(workspaceId, clientId, folder.id);
      toast.success('Folder deleted');
      if (currentFolderId === folder.id) setCurrentFolderId(null);
      loadFolders(); loadFiles();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  // ── File actions ────────────────────────────────────────────────
  const onFileMenu = async (action, file) => {
    if (action === 'preview') setPreviewFile(file);
    else if (action === 'download') {
      const token = localStorage.getItem('stellar_token');
      // D81b — see comment on _rewriteToBackend in ClientMediaDialogs.jsx.
      // The backend may embed a stale ingress host in download_url; we
      // always re-route through REACT_APP_BACKEND_URL so the browser hits
      // a host it can actually reach.
      const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
      let dlUrl = file.download_url;
      try {
        const u = new URL(file.download_url);
        dlUrl = `${base}${u.pathname}${u.search}`;
      } catch {
        if (dlUrl?.startsWith('/')) dlUrl = `${base}${dlUrl}`;
      }
      const r = await fetch(dlUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'omit',
      });
      if (!r.ok) { toast.error('Download failed'); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = file.filename; document.body.appendChild(a); a.click();
      setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 200);
    } else if (action === 'rename') setRenameDialog({ kind: 'file', target: file });
    else if (action === 'move') setMoveDialog({ file });
    else if (action === 'delete') setDeleteDialog({ kind: 'file', target: file });
    else if (action === 'share') {
      try {
        // D81b — Drive style: create the share at 'restricted' (default)
        // when first opened. User can flip to 'anyone' inside the dialog.
        const r = await clientMediaAPI.shareFile(workspaceId, clientId, file.id, 'restricted');
        setShareDialog({ kind: 'file', target: file, share: r.data });
        loadFiles();
      } catch (e) {
        toast.error('Failed to create share link');
      }
    }
  };

  // D81b — Folder sharing. Same Drive-style dialog reused.
  const openFolderShare = async (folder) => {
    try {
      const r = await clientMediaAPI.shareFolder(workspaceId, clientId, folder.id, 'restricted');
      setShareDialog({ kind: 'folder', target: folder, share: r.data });
      loadFolders();
    } catch (e) {
      toast.error('Failed to create share link');
    }
  };

  // Toggle share access for the currently open share dialog. Token doesn't
  // rotate, so the URL stays the same — only the access mode changes.
  const onShareSetAccess = async (access) => {
    if (!shareDialog) return;
    const { kind, target } = shareDialog;
    try {
      const r = kind === 'folder'
        ? await clientMediaAPI.shareFolder(workspaceId, clientId, target.id, access)
        : await clientMediaAPI.shareFile(workspaceId, clientId, target.id, access);
      setShareDialog({ kind, target, share: r.data });
      // Refresh list so the row badge reflects the new mode.
      if (kind === 'folder') loadFolders(); else loadFiles();
    } catch (e) {
      toast.error('Failed to update access');
    }
  };

  const onShareRevoke = async () => {
    if (!shareDialog) return;
    const { kind, target } = shareDialog;
    try {
      if (kind === 'folder') {
        await clientMediaAPI.revokeFolderShare(workspaceId, clientId, target.id);
        loadFolders();
      } else {
        await clientMediaAPI.revokeShare(workspaceId, clientId, target.id);
        loadFiles();
      }
      toast.success('Share link revoked');
    } catch (e) { toast.error('Revoke failed'); }
  };

  const doRename = async (target, newName) => {
    try {
      await clientMediaAPI.updateFile(workspaceId, clientId, target.id, { filename: newName });
      toast.success('Renamed');
      loadFiles();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Rename failed'); }
  };
  const doMove = async (file, folder_id) => {
    try {
      await clientMediaAPI.updateFile(workspaceId, clientId, file.id, { folder_id });
      toast.success('Moved');
      loadFiles();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Move failed'); }
  };
  const doDelete = async (target) => {
    try {
      await clientMediaAPI.deleteFile(workspaceId, clientId, target.id);
      toast.success('Deleted');
      loadFiles();
    } catch (e) { toast.error('Delete failed'); }
  };

  return (
    <div data-testid="d81-media-library" className="flex gap-5 h-full">
      {/* LEFT — folder rail */}
      <div className="w-56 shrink-0 border-r border-border/20 pr-3">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-[12px] font-semibold text-foreground/70 uppercase tracking-wide">Folders</h3>
          {canEdit && (
            <button
              onClick={() => setNewFolderDialog({ parent_id: null })}
              className="text-muted-foreground/60 hover:text-gold p-1"
              title="New folder"
              data-testid="d81-new-root-folder-btn"
            >
              <FolderPlus className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <FolderTree
          folders={folders}
          currentFolderId={currentFolderId}
          openSet={openSet}
          onToggle={onToggleFolder}
          onSelect={setCurrentFolderId}
          onCreateChild={(parent_id) => setNewFolderDialog({ parent_id })}
          onRename={(folder) => setRenameDialog({ kind: 'folder', target: folder })}
          onDelete={(folder) => setDeleteDialog({ kind: 'folder', target: folder })}
          onShare={openFolderShare}
          canEdit={canEdit}
        />
      </div>

      {/* RIGHT — file area */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Toolbar */}
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          {/* Breadcrumb */}
          <div className="flex items-center gap-1 text-[13px] text-muted-foreground flex-1 min-w-0">
            <button onClick={() => setCurrentFolderId(null)} className="hover:text-foreground" data-testid="d81-breadcrumb-root">All Media</button>
            {breadcrumb.map(b => (
              <span key={b.id} className="flex items-center gap-1 min-w-0">
                <ChevronRight className="h-3 w-3 shrink-0" />
                <button
                  onClick={() => setCurrentFolderId(b.id)}
                  className="hover:text-foreground truncate"
                  data-testid={`d81-breadcrumb-${b.id}`}
                >{b.name}</button>
              </span>
            ))}
          </div>
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground/50" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search files…"
              className="h-8 pl-7 text-[12px] w-44"
              data-testid="d81-search-input"
            />
          </div>
          {/* Month filter */}
          <Input
            type="month"
            value={filterMonth}
            onChange={(e) => setFilterMonth(e.target.value)}
            className="h-8 text-[12px] w-36"
            data-testid="d81-month-filter"
          />
          {/* Sort */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 text-[12px]" data-testid="d81-sort-btn">
                <ArrowUpDown className="h-3 w-3 mr-1" />
                {SORT_OPTIONS.find(o => o.value === sort)?.label || 'Sort'}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-card border-border/40">
              {SORT_OPTIONS.map(o => (
                <DropdownMenuItem key={o.value} onClick={() => setSort(o.value)} data-testid={`d81-sort-${o.value}`}>
                  {o.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          {/* View toggle */}
          <div className="flex items-center border border-border/30 rounded-md">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-1.5 ${viewMode === 'grid' ? 'bg-gold/10 text-gold' : 'text-muted-foreground/60'}`}
              data-testid="d81-view-grid"
              title="Grid"
            >
              <Grid3x3 className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-1.5 ${viewMode === 'list' ? 'bg-gold/10 text-gold' : 'text-muted-foreground/60'}`}
              data-testid="d81-view-list"
              title="List"
            >
              <ListIcon className="h-3.5 w-3.5" />
            </button>
          </div>
          {/* Import from approved submissions — all workspace roles */}
          <Button
            size="sm"
            variant="outline"
            className="h-8 text-[12px]"
            onClick={() => setImportDialogOpen(true)}
            data-testid="d90-import-open-btn"
          >
            <Import className="h-3 w-3 mr-1" />
            Add from approved
          </Button>
          {/* Upload */}
          {canEdit && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                hidden
                onChange={(e) => { handleUpload(e.target.files); e.target.value = ''; }}
              />
              <Button
                size="sm"
                className="h-8 text-[12px] bg-gold/10 hover:bg-gold/20 text-gold border-0"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                data-testid="d81-upload-btn"
              >
                <Upload className="h-3 w-3 mr-1" />
                {uploading ? 'Uploading…' : 'Upload'}
              </Button>
            </>
          )}
        </div>

        {/* Upload progress panel — per-file bars, live, no page refresh */}
        {uploadQueue.length > 0 && (() => {
          const done = uploadQueue.filter(u => u.status === 'done').length;
          const overall = Math.round(uploadQueue.reduce((s, u) => s + (u.status === 'done' ? 100 : u.pct), 0) / uploadQueue.length);
          return (
            <div
              className="mb-3 rounded-lg border border-gold/20 bg-gold/[0.04] px-3 py-2.5"
              data-testid="d81-upload-progress-panel"
            >
              <p className="text-[12px] font-medium text-foreground/80 mb-2" data-testid="d81-upload-progress-summary">
                {done === uploadQueue.length
                  ? `Uploaded ${done} of ${uploadQueue.length} file${uploadQueue.length > 1 ? 's' : ''}`
                  : `Uploading… ${done} of ${uploadQueue.length} file${uploadQueue.length > 1 ? 's' : ''} — ${overall}%`}
              </p>
              <div className="space-y-1.5 max-h-36 overflow-y-auto">
                {uploadQueue.map(u => (
                  <div key={u.key} data-testid={`d81-upload-progress-row-${u.key}`}>
                    <div className="flex items-center justify-between text-[11px] text-muted-foreground/70">
                      <span className="truncate flex-1 min-w-0">{u.name}</span>
                      <span className="tabular-nums ml-2 shrink-0">
                        {u.status === 'error' ? 'Failed' : u.status === 'done' ? '✓' : `${u.pct}%`}
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full bg-foreground/[0.06] overflow-hidden mt-0.5">
                      <div
                        className={`h-full transition-all ${u.status === 'error' ? 'bg-red-400' : 'bg-gold'}`}
                        style={{ width: `${u.status === 'done' ? 100 : u.pct}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Drag-drop area + grid/list */}
        <div
          onDragOver={(e) => { if (canEdit) { e.preventDefault(); e.currentTarget.classList.add('bg-gold/[0.04]'); } }}
          onDragLeave={(e) => { e.currentTarget.classList.remove('bg-gold/[0.04]'); }}
          onDrop={(e) => {
            if (!canEdit) return;
            e.preventDefault();
            e.currentTarget.classList.remove('bg-gold/[0.04]');
            handleUpload(e.dataTransfer?.files);
          }}
          className="flex-1 min-h-[300px] rounded-xl border-2 border-dashed border-border/15 p-3 transition-colors"
          data-testid="d81-files-area"
        >
          {loading ? (
            <p className="text-center text-[13px] text-muted-foreground/40 py-12">Loading…</p>
          ) : files.length === 0 ? (
            <div className="text-center py-12" data-testid="d81-files-empty">
              <Upload className="h-8 w-8 mx-auto text-muted-foreground/20 mb-2" />
              <p className="text-[13px] text-muted-foreground/40">No files here yet</p>
              {canEdit && <p className="text-[11px] text-muted-foreground/30 mt-1">Drag files in or click Upload (100MB cap)</p>}
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {files.map(f => (
                <FileCard key={f.id} file={f} viewMode="grid" onPreview={setPreviewFile} onMenu={onFileMenu} />
              ))}
            </div>
          ) : (
            <div className="space-y-1">
              {files.map(f => (
                <FileCard key={f.id} file={f} viewMode="list" onPreview={setPreviewFile} onMenu={onFileMenu} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* New folder dialog */}
      <NewFolderDialog
        open={!!newFolderDialog}
        parent_id={newFolderDialog?.parent_id || null}
        folders={folders}
        onClose={() => setNewFolderDialog(null)}
        onCreate={(name, pid) => { createFolder(name, pid); setNewFolderDialog(null); }}
      />
      {/* Rename dialog (folder or file) */}
      <RenameDialog
        open={!!renameDialog}
        target={renameDialog?.target}
        kind={renameDialog?.kind}
        onClose={() => setRenameDialog(null)}
        onSubmit={(name) => {
          if (renameDialog.kind === 'folder') renameFolder(renameDialog.target, name);
          else doRename(renameDialog.target, name);
          setRenameDialog(null);
        }}
      />
      {/* Move file dialog */}
      <MoveDialog
        open={!!moveDialog}
        file={moveDialog?.file}
        folders={folders}
        onClose={() => setMoveDialog(null)}
        onSubmit={(folder_id) => { doMove(moveDialog.file, folder_id); setMoveDialog(null); }}
      />
      {/* Delete confirm */}
      <DeleteConfirm
        open={!!deleteDialog}
        target={deleteDialog?.target}
        kind={deleteDialog?.kind}
        onClose={() => setDeleteDialog(null)}
        onConfirm={() => {
          if (deleteDialog.kind === 'folder') deleteFolder(deleteDialog.target);
          else doDelete(deleteDialog.target);
          setDeleteDialog(null);
        }}
      />
      {/* Share */}
      <ShareDialog
        open={!!shareDialog}
        kind={shareDialog?.kind || 'file'}
        target={shareDialog?.target}
        share={shareDialog?.share}
        onClose={() => setShareDialog(null)}
        onSetAccess={onShareSetAccess}
        onRevoke={onShareRevoke}
      />
      {/* Preview overlay */}
      <PreviewOverlay
        file={previewFile}
        onClose={() => setPreviewFile(null)}
      />
      {/* D90 — Import from approved submissions */}
      <ImportFromSubmissionsDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        workspaceId={workspaceId}
        clientId={clientId}
        currentFolderId={currentFolderId}
        onImported={() => { loadFiles({ silent: true }); }}
      />
    </div>
  );
}
